import { z } from "zod";

// ============================================================================
// WORKSPACE SCHEMAS (shared)
// ============================================================================

export const WorkspaceSummarySchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

export const WorkspacesResponseSchema = z.object({
  workspaces: z.array(WorkspaceSummarySchema),
});

export type WorkspaceSummary = z.infer<typeof WorkspaceSummarySchema>;
export type WorkspacesResponse = z.infer<typeof WorkspacesResponseSchema>;

// ============================================================================
// ENTITY FILTER OPTIONS
// ============================================================================

export const DatabricksClusterFilterOptionsSchema = z.object({
  cluster_names: z.array(z.string()).default([]),
  cluster_ids: z.array(z.string()).default([]),
});

export type DatabricksClusterFilterOptions = z.infer<typeof DatabricksClusterFilterOptionsSchema>;

export const WarehouseFilterOptionsSchema = z.object({
  warehouse_names: z.array(z.string()).default([]),
  warehouse_ids: z.array(z.string()).default([]),
});

export type WarehouseFilterOptions = z.infer<typeof WarehouseFilterOptionsSchema>;

// ============================================================================
// USER SCHEMAS
// ============================================================================

export const DatabricksUserTypeSchema = z.enum(["USER", "SERVICE_PRINCIPAL"]);

export type DatabricksUserType = z.infer<typeof DatabricksUserTypeSchema>;

export const DatabricksUserSchema = z.object({
  user_name: z.string(),
  user_type: DatabricksUserTypeSchema.nullable().optional(),
  total_queries: z.number().default(0),
  successful_queries: z.number().default(0),
  failed_queries: z.number().default(0),
  avg_duration_ms: z.number().default(0),
  max_duration_ms: z.number().nullable().optional(),
  total_duration_ms: z.number().default(0),
  total_read_bytes: z.number().default(0),
  total_spilled_bytes: z.number().default(0),
  last_query_time: z.string().nullable().optional(),
  unique_statement_types: z.number().default(0),
  total_dbus: z.number().default(0),
  total_cost: z.number().default(0),
});

export const UsersResponseSchema = z.object({
  users: z.array(DatabricksUserSchema),
  total_count: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
});

export type DatabricksUser = z.infer<typeof DatabricksUserSchema>;
export type UsersResponse = z.infer<typeof UsersResponseSchema>;

export const UserDetailSchema = z.object({
  user_name: z.string(),
  total_queries: z.number().default(0),
  successful_queries: z.number().default(0),
  failed_queries: z.number().default(0),
  avg_duration_ms: z.number().default(0),
  max_duration_ms: z.number().nullable().optional(),
  min_duration_ms: z.number().nullable().optional(),
  total_read_bytes: z.number().default(0),
  total_spilled_bytes: z.number().default(0),
  last_query_time: z.string().nullable().optional(),
  first_query_time: z.string().nullable().optional(),
  unique_statement_types: z.number().default(0),
  workspaces_used: z.number().default(0),
});

export const UserDetailResponseSchema = z.object({
  user: UserDetailSchema,
});

export type UserDetail = z.infer<typeof UserDetailSchema>;
export type UserDetailResponse = z.infer<typeof UserDetailResponseSchema>;

export const UserTrendPointSchema = z.object({
  period: z.string(),
  total_queries: z.number().default(0),
  successful_queries: z.number().default(0),
  failed_queries: z.number().default(0),
  avg_duration_ms: z.number().default(0),
  total_read_bytes: z.number().default(0),
});

export const UserTrendsResponseSchema = z.object({
  user_name: z.string(),
  trends: z.array(UserTrendPointSchema),
  granularity: z.string(),
});

export type UserTrendPoint = z.infer<typeof UserTrendPointSchema>;
export type UserTrendsResponse = z.infer<typeof UserTrendsResponseSchema>;

export const UserStatementBreakdownSchema = z.object({
  statement_type: z.string(),
  query_count: z.number().default(0),
  avg_duration_ms: z.number().default(0),
  total_read_bytes: z.number().default(0),
});

export const UserStatementBreakdownResponseSchema = z.object({
  user_name: z.string(),
  breakdown: z.array(UserStatementBreakdownSchema),
});

export type UserStatementBreakdown = z.infer<typeof UserStatementBreakdownSchema>;
export type UserStatementBreakdownResponse = z.infer<typeof UserStatementBreakdownResponseSchema>;

export const TopUserSchema = z.object({
  user_name: z.string(),
  total_queries: z.number().default(0),
  successful_queries: z.number().default(0),
  failed_queries: z.number().default(0),
});

export const TopUsersByQueriesResponseSchema = z.object({
  users: z.array(TopUserSchema),
});

export type TopUser = z.infer<typeof TopUserSchema>;
export type TopUsersByQueriesResponse = z.infer<typeof TopUsersByQueriesResponseSchema>;

export const TopUserByDataReadSchema = z.object({
  user_name: z.string(),
  total_read_bytes: z.number().default(0),
  total_queries: z.number().default(0),
  avg_duration_ms: z.number().default(0),
});

export const TopUsersByDataReadResponseSchema = z.object({
  users: z.array(TopUserByDataReadSchema),
});

export type TopUserByDataRead = z.infer<typeof TopUserByDataReadSchema>;
export type TopUsersByDataReadResponse = z.infer<typeof TopUsersByDataReadResponseSchema>;

// ============================================================================
// API PARAMETER TYPES
// ============================================================================

export interface DatabricksUsersParams {
  start_date?: string;
  end_date?: string;
  workspace_id?: string;
  limit?: number;
  user_names?: string[];
  user_types?: string[];
  page?: number;
  page_size?: number;
  sortAttribute?: string;
  sortOrder?: string;
}

export interface DatabricksUserDetailParams {
  user_name: string;
  start_date?: string;
  end_date?: string;
}

export interface DatabricksUserTrendsParams {
  user_name: string;
  start_date: string;
  end_date: string;
  granularity?: "day" | "week" | "month";
}

export const UserFilterOptionsSchema = z.object({
  user_names: z.array(z.string()).default([]),
});

export type UserFilterOptions = z.infer<typeof UserFilterOptionsSchema>;

export const ServicePrincipalConfigSchema = z.object({
  prefixes: z.array(z.string()).default([]),
  regexes: z.array(z.string()).default([]),
});

export type ServicePrincipalConfig = z.infer<typeof ServicePrincipalConfigSchema>;

// ============================================================================
// SERVING ENDPOINTS SCHEMAS
// ============================================================================

export const ServingEndpointSummarySchema = z.object({
  endpoint_name: z.string(),
  endpoint_id: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  task: z.string().nullable().optional(),
  served_entity_name: z.string().nullable().optional(),
  request_count: z.number().default(0),
  total_input_tokens: z.number().default(0),
  total_output_tokens: z.number().default(0),
  total_tokens: z.number().default(0),
  avg_input_tokens: z.number().default(0),
  avg_output_tokens: z.number().default(0),
  success_count: z.number().default(0),
  rate_limit_count: z.number().default(0),
  error_count: z.number().default(0),
  estimated_cost: z.number().default(0),
  cost_per_request: z.number().default(0),
  cost_per_1k_tokens: z.number().default(0),
});

export const ServingEndpointsOverviewSchema = z.object({
  endpoint_count: z.number().default(0),
  total_requests: z.number().default(0),
  total_input_tokens: z.number().default(0),
  total_output_tokens: z.number().default(0),
  total_tokens: z.number().default(0),
  total_cost: z.number().default(0),
  unique_users: z.number().default(0),
  cost_change_pct: z.number().nullable().optional(),
  // Billing costs from dbx_billing_usage
  model_serving_cost: z.number().nullable().optional(),
  vector_search_cost: z.number().nullable().optional(),
  total_billing_cost: z.number().nullable().optional(),
});

export const ServingEndpointsResponseSchema = z.object({
  overview: ServingEndpointsOverviewSchema,
  endpoints: z.array(ServingEndpointSummarySchema).default([]),
  pricing_note: z.string().optional(),
});

export type ServingEndpointSummary = z.infer<typeof ServingEndpointSummarySchema>;
export type ServingEndpointsOverview = z.infer<typeof ServingEndpointsOverviewSchema>;
export type ServingEndpointsResponse = z.infer<typeof ServingEndpointsResponseSchema>;

export const ServingEndpointDetailSchema = z.object({
  endpoint_name: z.string(),
  endpoint_id: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  task: z.string().nullable().optional(),
  served_entity_name: z.string().nullable().optional(),
  created_by: z.string().nullable().optional(),
  request_count: z.number().default(0),
  total_input_tokens: z.number().default(0),
  total_output_tokens: z.number().default(0),
  total_cost: z.number().default(0),
  input_token_cost: z.number().default(0),
  output_token_cost: z.number().default(0),
  cost_per_day: z.number().default(0),
  cost_per_request: z.number().default(0),
  avg_input_tokens: z.number().default(0),
  avg_output_tokens: z.number().default(0),
  success_rate: z.number().default(0),
  first_request: z.string().nullable().optional(),
  last_request: z.string().nullable().optional(),
});

export const UsageTrendPointSchema = z.object({
  period: z.string(),
  request_count: z.number().default(0),
  input_tokens: z.number().default(0),
  output_tokens: z.number().default(0),
  total_tokens: z.number().default(0),
  cost: z.number().default(0),
  success_count: z.number().default(0),
  error_count: z.number().default(0),
});

export const StatusDistributionSchema = z.object({
  status_code: z.number(),
  count: z.number(),
});

export const ServingEndpointDetailResponseSchema = z.object({
  endpoint: ServingEndpointDetailSchema,
  usage_trends: z.array(UsageTrendPointSchema).default([]),
  status_distribution: z.array(StatusDistributionSchema).default([]),
  pricing_note: z.string().optional(),
});

export type ServingEndpointDetail = z.infer<typeof ServingEndpointDetailSchema>;
export type UsageTrendPoint = z.infer<typeof UsageTrendPointSchema>;
export type StatusDistribution = z.infer<typeof StatusDistributionSchema>;
export type ServingEndpointDetailResponse = z.infer<typeof ServingEndpointDetailResponseSchema>;

export const EndpointUsageTrendSchema = z.object({
  endpoint_name: z.string(),
  trends: z.array(UsageTrendPointSchema).default([]),
});

export const OverallUsageTrendsResponseSchema = z.object({
  granularity: z.string().default("day"),
  trends: z.array(UsageTrendPointSchema).default([]),
  by_endpoint: z.array(EndpointUsageTrendSchema).default([]),
});

export type EndpointUsageTrend = z.infer<typeof EndpointUsageTrendSchema>;
export type OverallUsageTrendsResponse = z.infer<typeof OverallUsageTrendsResponseSchema>;

export const UserCostAttributionSchema = z.object({
  requester: z.string(),
  request_count: z.number().default(0),
  input_tokens: z.number().default(0),
  output_tokens: z.number().default(0),
  total_tokens: z.number().default(0),
  cost: z.number().default(0),
  pct_of_total: z.number().default(0),
});

export const EndpointUsersResponseSchema = z.object({
  endpoint_name: z.string(),
  users: z.array(UserCostAttributionSchema).default([]),
  total_cost: z.number().default(0),
});

export const CostAttributionResponseSchema = z.object({
  total_cost: z.number().default(0),
  by_endpoint: z.array(ServingEndpointSummarySchema).default([]),
  by_user: z.array(UserCostAttributionSchema).default([]),
  trends: z.array(UsageTrendPointSchema).default([]),
  pricing_note: z.string().optional(),
});

export type UserCostAttribution = z.infer<typeof UserCostAttributionSchema>;
export type EndpointUsersResponse = z.infer<typeof EndpointUsersResponseSchema>;
export type CostAttributionResponse = z.infer<typeof CostAttributionResponseSchema>;

export interface DatabricksServingParams {
  start_date: string;
  end_date: string;
  workspace_id?: string;
}

export interface DatabricksServingTrendsParams extends DatabricksServingParams {
  granularity?: "day" | "week" | "month";
}

export interface DatabricksServingDetailParams {
  endpoint_name: string;
  start_date: string;
  end_date: string;
}

export interface DatabricksServingUsersParams extends DatabricksServingDetailParams {
  limit?: number;
}

// ============================================================================
// QUERY SCHEMAS
// ============================================================================

export const QuerySummarySchema = z.object({
  total_queries: z.number().optional(),
  unique_statement_types: z.number().optional(),
  successful_queries: z.number().optional(),
  failed_queries: z.number().optional(),
  avg_duration_ms: z.number().optional(),
  total_read_bytes: z.number().optional(),
  total_spilled_bytes: z.number().optional(),
  total_cost: z.number().optional(),
});

export const QuerySummaryResponseSchema = z.object({
  summary: QuerySummarySchema.nullable(),
});

export type QuerySummaryResponse = z.infer<typeof QuerySummaryResponseSchema>;

export const QueryTrendPointSchema = z.object({
  period: z.string(),
  total_queries: z.number().optional(),
  successful_queries: z.number().optional(),
  failed_queries: z.number().optional(),
  avg_duration_ms: z.number().optional(),
  total_read_bytes: z.number().optional(),
});

export const QueryTrendsResponseSchema = z.object({
  trends: z.array(QueryTrendPointSchema),
  granularity: z.string().optional(),
});

export type QueryTrendPoint = z.infer<typeof QueryTrendPointSchema>;
export type QueryTrendsResponse = z.infer<typeof QueryTrendsResponseSchema>;

export const QueryItemSchema = z.object({
  statement_id: z.string(),
  statement_type: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  total_duration_ms: z.number().nullable().optional(),
  execution_duration_ms: z.number().nullable().optional(),
  read_bytes: z.number().nullable().optional(),
  write_bytes: z.number().nullable().optional(),
  spilled_local_bytes: z.number().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
});

export const QueriesResponseSchema = z.object({
  queries: z.array(QueryItemSchema),
  total_count: z.number(),
  page: z.number(),
  page_size: z.number(),
});

export type QueryItem = z.infer<typeof QueryItemSchema>;
export type QueriesResponse = z.infer<typeof QueriesResponseSchema>;

const QueryBaseFields = {
  statement_id: z.string(),
  statement_type: z.string().nullable().optional(),
  execution_status: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  end_time: z.string().nullable().optional(),
  total_duration_ms: z.number().nullable().optional(),
  read_bytes: z.number().nullable().optional(),
  spilled_local_bytes: z.number().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  user_name: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
};

export const SlowQuerySchema = z.object(QueryBaseFields);
export const SpillQuerySchema = z.object(QueryBaseFields);
export const FullScanQuerySchema = z.object(QueryBaseFields);
export const FailedQuerySchema = z.object(QueryBaseFields);

export const SlowQueriesResponseSchema = z.object({
  queries: z.array(SlowQuerySchema),
  min_duration_ms: z.number().optional(),
  total_count: z.number().optional(),
});
export const SpillQueriesResponseSchema = z.object({
  queries: z.array(SpillQuerySchema),
  min_spill_bytes: z.number().optional(),
  total_count: z.number().optional(),
});
export const FullScanQueriesResponseSchema = z.object({
  queries: z.array(FullScanQuerySchema),
  min_read_bytes: z.number().optional(),
  total_count: z.number().optional(),
});
export const FailedQueriesResponseSchema = z.object({
  queries: z.array(FailedQuerySchema),
  total_count: z.number().optional(),
});

export type SlowQueriesResponse = z.infer<typeof SlowQueriesResponseSchema>;
export type SpillQueriesResponse = z.infer<typeof SpillQueriesResponseSchema>;
export type FullScanQueriesResponse = z.infer<typeof FullScanQueriesResponseSchema>;
export type FailedQuery = z.infer<typeof FailedQuerySchema>;
export type FailedQueriesResponse = z.infer<typeof FailedQueriesResponseSchema>;

export const StatementTypeStatsSchema = z.object({
  statement_type: z.string(),
  query_count: z.number(),
  avg_duration_ms: z.number(),
  total_read_bytes: z.number(),
});

export const StatementTypeBreakdownResponseSchema = z.object({
  breakdown: z.array(StatementTypeStatsSchema),
});

export type StatementTypeStats = z.infer<typeof StatementTypeStatsSchema>;
export type StatementTypeBreakdownResponse = z.infer<typeof StatementTypeBreakdownResponseSchema>;

export const UserQueryStatsSchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  query_count: z.number(),
  avg_duration_ms: z.number(),
  total_read_bytes: z.number(),
});

export const UserQueryBreakdownResponseSchema = z.object({
  breakdown: z.array(UserQueryStatsSchema),
});

export type UserQueryStats = z.infer<typeof UserQueryStatsSchema>;
export type UserQueryBreakdownResponse = z.infer<typeof UserQueryBreakdownResponseSchema>;

// Unified queries with insights
export const UnifiedQuerySchema = z.object({
  statement_id: z.string(),
  statement_text: z.string().nullable().optional(),
  statement_type: z.string().nullable().optional(),
  execution_status: z.string().nullable().optional(),
  start_time: z.string().nullable(),
  end_time: z.string().nullable().optional(),
  total_duration_ms: z.number().nullable().optional(),
  read_bytes: z.number().nullable().optional(),
  spilled_local_bytes: z.number().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  cluster_id: z.string().nullable().optional(),
  warehouse_name: z.string().nullable().optional(),
  warehouse_size: z.string().nullable().optional(),
  cluster_name: z.string().nullable().optional(),
  // DLT pipeline + job ids extracted from the backend's ``query_source``.
  // Serverless REFRESH on DLT has neither ``warehouse_id`` nor
  // ``cluster_id`` (managed compute); the Compute column falls back to
  // the pipeline / job label.
  pipeline_id: z.string().nullable().optional(),
  pipeline_name: z.string().nullable().optional(),
  job_id: z.string().nullable().optional(),
  // Resolved Compute label composed by the backend (priority cascade:
  // warehouse name → warehouse id → cluster name → cluster id →
  // ``DLT Pipeline: <name|id>`` → ``Job: <id>`` → ``-``). The list
  // cell, the detail metric, and any future CSV export read the same
  // value so the three surfaces can't drift. Job branch shows the
  // bare id since the BE no longer joins the job inventory.
  compute_display: z.string().nullable().optional(),
  estimated_cost_usd: z.number().nullable().optional(),
  // DBUs attributed to this query — same fair-share fallback as cost.
  // Preserved in the schema so consumers (tooltips, exports) keep the
  // backend value instead of getting silently stripped by Zod.
  dbus: z.number().nullable().optional(),
  user_name: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  insights: z.array(z.string()).default([]),
});

export const UnifiedQueriesResponseSchema = z.object({
  queries: z.array(UnifiedQuerySchema),
  total_count: z.number(),
  page: z.number().default(1),
  page_size: z.number().default(100),
  total_pages: z.number().default(1),
});

export type UnifiedQuery = z.infer<typeof UnifiedQuerySchema>;
export type UnifiedQueriesResponse = z.infer<typeof UnifiedQueriesResponseSchema>;

// Query detail with full metrics
export const QueryDetailSchema = z.object({
  statement_id: z.string(),
  statement_text: z.string().nullable().optional(),
  statement_type: z.string().nullable().optional(),
  execution_status: z.string().nullable().optional(),
  start_time: z.string().nullable(),
  end_time: z.string().nullable().optional(),
  total_duration_ms: z.number().nullable().optional(),
  waiting_for_compute_duration_ms: z.number().nullable().optional(),
  waiting_at_capacity_duration_ms: z.number().nullable().optional(),
  execution_duration_ms: z.number().nullable().optional(),
  compilation_duration_ms: z.number().nullable().optional(),
  read_bytes: z.number().nullable().optional(),
  written_bytes: z.number().nullable().optional(),
  read_rows: z.number().nullable().optional(),
  produced_rows: z.number().nullable().optional(),
  read_partitions: z.number().nullable().optional(),
  total_partitions: z.number().nullable().optional(),
  read_files: z.number().nullable().optional(),
  spilled_local_bytes: z.number().nullable().optional(),
  spilled_remote_bytes: z.number().nullable().optional(),
  result_fetch_duration_ms: z.number().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  warehouse_name: z.string().nullable().optional(),
  warehouse_size: z.string().nullable().optional(),
  // Same identifier fallback chain the unified queries Compute column
  // uses. The detail-page Compute metric reads ``compute_display``
  // (composed in the backend) so the metric and the list cell stay in
  // lockstep.
  cluster_id: z.string().nullable().optional(),
  cluster_name: z.string().nullable().optional(),
  pipeline_id: z.string().nullable().optional(),
  pipeline_name: z.string().nullable().optional(),
  job_id: z.string().nullable().optional(),
  compute_display: z.string().nullable().optional(),
  estimated_cost_usd: z.number().nullable().optional(),
  // DBUs attributed to this query (same fair-share fallback as cost).
  dbus: z.number().nullable().optional(),
  user_name: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  query_hash: z.string().nullable().optional(),
  canUseResultCache: z.boolean().nullable().optional(),
  resultFetchedFromCache: z.boolean().nullable().optional(),
  insights: z.array(z.string()).default([]),
});

export const QueryDetailResponseSchema = z.object({
  query: QueryDetailSchema,
});

export type QueryDetail = z.infer<typeof QueryDetailSchema>;
export type QueryDetailResponse = z.infer<typeof QueryDetailResponseSchema>;

export const QuerySqlLineageResponseSchema = z.object({
  tableEdges: z.array(z.tuple([z.string(), z.string()])),
  // eslint-disable-next-line no-restricted-syntax -- lineage details have dynamic structure from backend
  details: z.record(z.any()),
  nodePositions: z
    .record(z.tuple([z.number(), z.number()]))
    .nullable()
    .optional(),
  is_lineage: z.boolean(),
  is_error: z.boolean(),
});

export type QuerySqlLineageResponse = z.infer<typeof QuerySqlLineageResponseSchema>;

// ============================================================================
// QUERY API PARAMETER TYPES
// ============================================================================

export interface DatabricksQueriesParams {
  start_date: string;
  end_date: string;
  workspace_id?: string;
  warehouse_id?: string;
  /** Multi-workspace filter — applied to both summary and table. */
  workspace_ids?: string[];
  /** Unified Compute filter — prefixed ``{kind}:{id}`` values covering
   *  warehouse, cluster, and pipeline. Applied to both summary and table. */
  compute_id?: string[];
  statement_type?: string;
  /** Multi-select user filter. Applied to both summary and table. */
  user_name?: string[];
  statement_ids?: string[];
  page?: number;
  page_size?: number;
}

export interface DatabricksUnifiedQueriesParams extends Omit<
  DatabricksQueriesParams,
  "warehouse_id"
> {
  /** Legacy single-kind column filter (warehouse-only). Kept for
   *  back-compat with bookmarks; new code should populate ``compute_id``
   *  with prefixed ``{kind}:{id}`` values from the unified dropdown. */
  warehouse_id?: string[];
  insights?: string[];
  sort_by?: string;
  sort_order?: string;
}

export interface QueryStatementIdSearchParams {
  start_date: string;
  end_date: string;
  workspace_id?: string;
  prefix?: string;
  page?: number;
  page_size?: number;
}

// ============================================================================
// QUERY FILTER OPTIONS
// ============================================================================

export const QueryFilterIdNameSchema = z.object({
  id: z.string(),
  name: z.string(),
});

// Unified Compute filter dropdown entry. ``kind`` namespaces ``id`` so a
// warehouse_id and a cluster_id can both be selected without collision;
// ``display`` matches the table's ``compute_display`` column rendering
// (e.g. ``Analytics Warehouse``, ``DLT Pipeline: events-dlt``) so the
// dropdown label and the cell read identically.
export const ComputeFilterOptionSchema = z.object({
  kind: z.enum(["warehouse", "cluster", "pipeline"]),
  id: z.string(),
  display: z.string(),
});

export const QueryFilterOptionsSchema = z.object({
  statement_types: z.array(z.string()).default([]),
  user_names: z.array(z.string()).default([]),
  warehouses: z.array(QueryFilterIdNameSchema).default([]),
  // Unified Compute dropdown — warehouse + cluster + pipeline (job
  // is intentionally excluded BE-side as high-cardinality + repetitive).
  // ``display`` matches the table's Compute column rendering so the
  // dropdown label reads identically to the cell.
  computes: z.array(ComputeFilterOptionSchema).default([]),
  workspaces: z.array(QueryFilterIdNameSchema).default([]),
});

export type QueryFilterOptions = z.infer<typeof QueryFilterOptionsSchema>;
export type QueryFilterIdName = z.infer<typeof QueryFilterIdNameSchema>;
export type ComputeFilterOption = z.infer<typeof ComputeFilterOptionSchema>;

export const QueryStatementIdsResponseSchema = z.object({
  statement_ids: z.array(z.string()).default([]),
  // Forward-looking page bound consumed by the filter dropdown's
  // `page < pages` check (current page, or current + 1 while more remain).
  total_pages: z.number().default(1),
});

export type QueryStatementIdsResponse = z.infer<typeof QueryStatementIdsResponseSchema>;

// Dedicated single-list search responses — separate endpoints (not the
// aggregate /queries/filters) so the typed prefix only ever scans the one
// list it belongs to.
export const QueryUserNamesResponseSchema = z.object({
  user_names: z.array(z.string()).default([]),
  total_pages: z.number().default(1),
});

export type QueryUserNamesResponse = z.infer<typeof QueryUserNamesResponseSchema>;

export const QueryComputesResponseSchema = z.object({
  computes: z.array(ComputeFilterOptionSchema).default([]),
  total_pages: z.number().default(1),
});

export type QueryComputesResponse = z.infer<typeof QueryComputesResponseSchema>;

export const QueryWorkspacesResponseSchema = z.object({
  workspaces: z.array(QueryFilterIdNameSchema).default([]),
  total_pages: z.number().default(1),
});

export type QueryWorkspacesResponse = z.infer<typeof QueryWorkspacesResponseSchema>;
